import { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { SceneLogo, ScenePrepare, SceneSimulate, SceneSupervise, SceneEvaluate } from './AnimatedDemo';
import { Play, Pause, RotateCcw, Volume2, VolumeX, Maximize, Users, BarChart3, Building2, GraduationCap, Stethoscope, Cpu, Music, Mic } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DURATION = 122;

const SUBS = [
  [0, 6.0, 'Las decisiones clínicas se entrenan antes de enfrentarlas en la vida real.'],
  [6.0, 16.1, 'SimuClinic integra preparación, simulación, supervisión y evaluación en una plataforma diseñada para la formación de profesionales de la salud.'],
  [16.1, 29.8, 'El instructor puede crear, configurar o reutilizar escenarios clínicos, definiendo objetivos, signos vitales, medicamentos, acciones, tratamientos, eventos y criterios de evaluación.'],
  [29.8, 42.8, 'Durante la simulación, el estudiante analiza al paciente, solicita exámenes, administra tratamientos y toma decisiones que modifican dinámicamente su evolución.'],
  [42.8, 54.8, 'El instructor puede conectarse a uno o varios estudiantes, observar cada decisión en tiempo real e intervenir cuando el escenario lo requiera.'],
  [54.8, 64.8, 'Al finalizar, cada acción queda registrada para evaluar el desempeño e identificar oportunidades de mejora.'],
  [64.8, 72.8, 'El debriefing permite analizar cada decisión y transformar la experiencia en aprendizaje.'],
  [72.8, 81.3, 'SimuClinic permite visualizar resultados, evolución y desempeño para medir el progreso de cada participante.'],
  [81.3, 90.8, 'Su arquitectura multiestablecimiento permite administrar usuarios, sedes, escenarios y resultados desde un entorno centralizado.'],
  [90.8, 101.6, 'SimuClinic también incorpora una modalidad individual para médicos que desean practicar mediante casos clínicos interactivos orientados a su preparación EUNACOM.'],
  [101.6, 109.1, 'La plataforma evoluciona junto a un equipo multidisciplinario de profesionales de la salud e ingenieros.'],
  [109.1, 116.7, 'SimuClinic. Más práctica, mejores decisiones y menos riesgos para aprender.'],
  [116.7, 122, 'Escanea el código y solicita tu demostración.'],
];

const Panel = ({ children, d = 0, className = '' }) => (
  <div className={`bg-slate-800/90 border border-slate-700 rounded-xl p-3 ${className}`} style={{ animation: `popIn 0.55s cubic-bezier(0.22,1,0.36,1) ${d}s both` }}>{children}</div>
);

const EcgLine = () => (
  <svg className="absolute top-3 left-0 w-full h-8 opacity-25 pointer-events-none" viewBox="0 0 600 40" preserveAspectRatio="none">
    <path d="M0,20 L80,20 L95,20 L102,6 L110,34 L118,20 L200,20 L215,20 L222,6 L230,34 L238,20 L340,20 L355,20 L362,6 L370,34 L378,20 L470,20 L485,20 L492,6 L500,34 L508,20 L600,20"
      fill="none" stroke="#22d3ee" strokeWidth="1.5" strokeDasharray="900" strokeDashoffset="900"
      style={{ animation: 'ecgDraw 5s linear infinite' }} />
  </svg>
);

const Blobs = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden">
    <div className="absolute -top-16 -left-16 w-56 h-56 rounded-full bg-cyan-500/10 blur-3xl" style={{ animation: 'floatA 9s ease-in-out infinite' }} />
    <div className="absolute -bottom-20 -right-10 w-64 h-64 rounded-full bg-blue-600/10 blur-3xl" style={{ animation: 'floatB 11s ease-in-out infinite' }} />
    <div className="absolute top-1/3 right-1/4 w-40 h-40 rounded-full bg-purple-500/8 blur-3xl" style={{ animation: 'floatA 13s ease-in-out infinite reverse' }} />
  </div>
);

const SceneOverview = () => (
  <div className="h-full p-4 md:p-8 flex flex-col justify-center gap-3">
    <div className="text-cyan-400 text-xs font-bold uppercase tracking-widest" style={{ animation: 'popIn 0.5s ease both' }}>Una sola plataforma</div>
    <div className="grid grid-cols-3 gap-2 md:gap-3">
      {[['Preparar', 'Escenarios clínicos'], ['Simular', 'Decisiones reales'], ['Supervisar', 'Tiempo real'],
        ['Evaluar', 'Cada acción'], ['Debriefing', 'Aprendizaje'], ['Progreso', 'Analítica']].map(([t, s], i) => (
        <Panel key={t} d={i * 0.18} className="hover:border-cyan-500/50">
          <div className="text-white text-xs md:text-sm font-bold">{t}</div>
          <div className="text-slate-400 text-[9px] md:text-[11px]">{s}</div>
        </Panel>
      ))}
    </div>
  </div>
);

const SceneDebrief = () => (
  <div className="h-full p-4 md:p-8 flex flex-col justify-center gap-2">
    <div className="text-amber-400 text-xs font-bold uppercase tracking-widest">Debriefing</div>
    {[['02:14', 'Oxigenoterapia iniciada', 'correcta'], ['03:40', 'Salbutamol 5 mg NEB', 'correcta'],
      ['05:02', 'Dosis fuera de rango', 'error'], ['06:30', 'Retroalimentación del instructor', 'nota']].map(([t, e, k], i) => (
      <Panel key={t} d={i * 0.28}>
        <div className="flex items-center gap-2 text-[10px] md:text-xs">
          <span className="text-cyan-500 font-mono">{t}</span>
          <span className="text-white">{e}</span>
          <span className={`ml-auto px-2 py-0.5 rounded-full text-[8px] md:text-[10px] ${k === 'error' ? 'bg-rose-500/20 text-rose-300' : k === 'nota' ? 'bg-purple-500/20 text-purple-300' : 'bg-emerald-500/20 text-emerald-300'}`}>{k}</span>
        </div>
      </Panel>
    ))}
  </div>
);

const SceneHistorial = ({ local }) => {
  const p = Math.min(1, local / 2.5);
  const stats = [[Math.round(24 * p), 'Simulaciones'], [Math.round(87 * p), 'Puntaje promedio'], [`+${Math.round(12 * p)}%`, 'Evolución']];
  return (
    <div className="h-full p-4 md:p-8 flex flex-col justify-center gap-3">
      <div className="text-emerald-400 text-xs font-bold uppercase tracking-widest flex items-center gap-2"><BarChart3 size={14} /> Mide el progreso</div>
      <div className="grid grid-cols-3 gap-2">
        {stats.map(([v, l], i) => (
          <Panel key={l} d={i * 0.2}><div className="text-xl md:text-3xl font-bold text-white font-mono">{v}</div><div className="text-[9px] md:text-[11px] text-slate-400">{l}</div></Panel>
        ))}
      </div>
      <div className="flex items-end gap-1.5 h-14 md:h-20">
        {[40, 55, 48, 62, 70, 66, 78, 85, 82, 90].map((h, i) => (
          <div key={i} className="flex-1 rounded-t bg-gradient-to-t from-cyan-600 to-cyan-400"
            style={{ height: `${h}%`, transformOrigin: 'bottom', animation: `barUp 0.7s cubic-bezier(0.22,1,0.36,1) ${0.4 + i * 0.12}s both` }} />
        ))}
      </div>
    </div>
  );
};

const SceneMulti = () => (
  <div className="h-full p-4 md:p-8 flex flex-col justify-center gap-3">
    <div className="text-purple-400 text-xs font-bold uppercase tracking-widest flex items-center gap-2"><Building2 size={14} /> Multiestablecimiento</div>
    <div className="grid grid-cols-3 gap-2">
      {['Hospital Central', 'Universidad Demo', 'Clínica Norte'].map((s, i) => (
        <Panel key={s} d={i * 0.22}>
          <Building2 className="w-4 h-4 text-cyan-400 mb-1" />
          <div className="text-white text-[10px] md:text-xs font-semibold">{s}</div>
          <div className="text-slate-500 text-[9px]">Usuarios · Cursos · Escenarios</div>
        </Panel>
      ))}
    </div>
    <Panel d={0.7}><div className="flex items-center gap-2 text-[10px] md:text-xs text-slate-300"><Users size={14} className="text-cyan-400" /> Roles y permisos centralizados · Resultados por sede</div></Panel>
  </div>
);

const SceneEunacom = () => (
  <div className="h-full p-4 md:p-8 flex flex-col justify-center gap-3">
    <div className="text-cyan-400 text-xs font-bold uppercase tracking-widest flex items-center gap-2"><GraduationCap size={14} /> Preparación EUNACOM</div>
    <div className="grid grid-cols-3 gap-2">
      {[['120+', 'Casos clínicos'], ['Interactivos', 'Con evolución'], ['Estadísticas', 'Por especialidad']].map(([v, l], i) => (
        <Panel key={l} d={i * 0.22}><div className="text-sm md:text-xl font-bold text-white">{v}</div><div className="text-[9px] md:text-[11px] text-slate-400">{l}</div></Panel>
      ))}
    </div>
    <Panel d={0.7}><div className="text-[10px] md:text-xs text-slate-300">Modalidad individual para médicos · práctica autónoma con retroalimentación</div></Panel>
  </div>
);

const SceneCierreExt = ({ showLogo }) => (
  <div className="h-full flex flex-col items-center justify-center gap-3">
    {!showLogo ? (
      <div className="flex items-center gap-4" style={{ animation: 'popIn 0.5s ease both' }}>
        <Stethoscope className="w-8 h-8 text-cyan-400" /><Cpu className="w-8 h-8 text-purple-400" />
        <span className="text-white text-sm md:text-base font-semibold">Profesionales de la salud e ingenieros</span>
      </div>
    ) : (
      <div className="flex flex-col items-center gap-2" style={{ animation: 'popIn 0.6s ease both' }}>
        <SceneLogo />
        <p className="text-slate-300 text-xs md:text-sm -mt-2">Más práctica. Mejores decisiones. Menos riesgos para aprender.</p>
      </div>
    )}
  </div>
);

const SceneQRFinal = () => (
  <div className="h-full flex flex-col items-center justify-center gap-3" style={{ animation: 'popIn 0.7s ease both' }}>
    <div className="bg-white rounded-2xl p-3 shadow-lg" style={{ animation: 'qrPulse 2.2s ease-in-out infinite' }}>
      <img src="/qr-simuclinic.png" alt="QR SimuClinic" className="w-28 h-28 md:w-40 md:h-40" data-testid="demo-ext-qr" />
    </div>
    <p className="text-cyan-300 text-sm md:text-base font-bold text-center">Escanea y solicita tu demostración</p>
    <p className="text-slate-400 text-xs md:text-sm">www.simuclinic.cl</p>
  </div>
);

const AnimatedDemoExtended = forwardRef(function AnimatedDemoExtended({ loop = true, withAudio = true, capture = false }, ref) {
  const [t, setT] = useState(0);
  const [playing, setPlaying] = useState(!capture);
  const [started, setStarted] = useState(!capture);
  const [muted, setMuted] = useState(true);
  const [audioOk, setAudioOk] = useState(false);
  const [musicOk, setMusicOk] = useState(false);
  const [volVoice, setVolVoice] = useState(1.0);
  const [volMusic, setVolMusic] = useState(0.20);
  const raf = useRef(null); const last = useRef(null); const timeRef = useRef(0);
  const audioRef = useRef(null); const musicRef = useRef(null); const boxRef = useRef(null);

  useEffect(() => {
    if (!withAudio) return;
    fetch(`${API_URL}/api/videos/assets-preview/demo_narracion`, { method: 'HEAD' }).then(r => setAudioOk(r.ok)).catch(() => {});
    fetch(`${API_URL}/api/videos/assets-preview/musica`, { method: 'HEAD' }).then(r => setMusicOk(r.ok)).catch(() => {});
  }, [withAudio]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volVoice;
  }, [volVoice, audioOk]);
  useEffect(() => {
    if (musicRef.current) musicRef.current.volume = volMusic;
  }, [volMusic, musicOk]);

  const syncPlay = useCallback((isMuted) => {
    [audioRef.current, musicRef.current].forEach(a => { if (a && !isMuted) a.play().catch(() => {}); });
  }, []);

  useEffect(() => {
    if (!playing) {
      cancelAnimationFrame(raf.current); last.current = null;
      audioRef.current?.pause(); musicRef.current?.pause();
      return;
    }
    syncPlay(muted);
    const tick = (ts) => {
      if (last.current == null) last.current = ts;
      const dt = (ts - last.current) / 1000; last.current = ts;
      let next = timeRef.current + dt;
      if (next >= DURATION) {
        if (loop) {
          next = 0;
          [audioRef.current, musicRef.current].forEach(a => { if (a) { a.currentTime = 0; if (!muted) a.play().catch(() => {}); } });
        } else { next = DURATION; setPlaying(false); }
      }
      timeRef.current = next; setT(next);
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [playing, loop, muted, syncPlay]);

  const restart = useCallback(() => {
    timeRef.current = 0; setT(0); setPlaying(true);
    [audioRef.current, musicRef.current].forEach(a => { if (a) { a.currentTime = 0; if (!muted) a.play().catch(() => {}); } });
  }, [muted]);

  const unmute = useCallback(() => {
    setMuted(false);
    [audioRef.current, musicRef.current].forEach(a => { if (a) a.muted = false; });
    if (audioRef.current) { audioRef.current.volume = volVoice; audioRef.current.currentTime = Math.min(timeRef.current, audioRef.current.duration || timeRef.current); }
    if (musicRef.current) { musicRef.current.volume = volMusic; musicRef.current.currentTime = timeRef.current % (musicRef.current.duration || DURATION); }
    syncPlay(false);
  }, [volVoice, volMusic, syncPlay]);

  const toggleSound = () => {
    if (muted) { unmute(); return; }
    setMuted(true);
    [audioRef.current, musicRef.current].forEach(a => { if (a) a.muted = true; });
  };

  useImperativeHandle(ref, () => ({ enableAudio: unmute }), [unmute]);

  const startCapture = () => { setStarted(true); restart(); };

  const scene = t < 6.0 ? <SceneLogo /> : t < 16.1 ? <SceneOverview /> : t < 29.8 ? <ScenePrepare />
    : t < 42.8 ? <SceneSimulate t={t} /> : t < 54.8 ? <SceneSupervise /> : t < 64.8 ? <SceneEvaluate />
    : t < 72.8 ? <SceneDebrief /> : t < 81.3 ? <SceneHistorial local={t - 72.8} /> : t < 90.8 ? <SceneMulti />
    : t < 101.6 ? <SceneEunacom /> : t < 116.7 ? <SceneCierreExt showLogo={t >= 109.1} /> : <SceneQRFinal />;
  const sceneKey = [6.0, 16.1, 29.8, 42.8, 54.8, 64.8, 72.8, 81.3, 90.8, 101.6, 109.1, 116.7, 123].findIndex(x => t < x);
  const sub = SUBS.find(([a, b]) => t >= a && t < b)?.[2] || SUBS[SUBS.length - 1][2];
  const hasSound = withAudio && (audioOk || musicOk);

  return (
    <div ref={boxRef} className={`relative bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950 overflow-hidden ${capture ? '' : 'aspect-video rounded-lg'}`} style={capture ? { zoom: 2, width: 960, height: 540 } : undefined} data-testid="animated-demo-extended">
      <style>{`
        @keyframes slideUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
        @keyframes growBar{from{width:0}}
        @keyframes popIn{from{opacity:0;transform:translateY(14px) scale(0.96)}to{opacity:1;transform:translateY(0) scale(1)}}
        @keyframes sceneIn{from{opacity:0;transform:scale(1.03)}to{opacity:1;transform:scale(1)}}
        @keyframes barUp{from{transform:scaleY(0)}to{transform:scaleY(1)}}
        @keyframes ecgDraw{from{stroke-dashoffset:900}to{stroke-dashoffset:-900}}
        @keyframes floatA{0%,100%{transform:translate(0,0)}50%{transform:translate(22px,16px)}}
        @keyframes floatB{0%,100%{transform:translate(0,0)}50%{transform:translate(-18px,-20px)}}
        @keyframes qrPulse{0%,100%{transform:scale(1);box-shadow:0 0 18px rgba(34,211,238,0.35)}50%{transform:scale(1.04);box-shadow:0 0 30px rgba(34,211,238,0.6)}}
        @keyframes glowBar{0%,100%{filter:brightness(1)}50%{filter:brightness(1.5)}}
        input[type=range].demo-vol{-webkit-appearance:none;appearance:none;width:56px;height:3px;border-radius:2px;background:#334155;outline:none}
        input[type=range].demo-vol::-webkit-slider-thumb{-webkit-appearance:none;appearance:none;width:10px;height:10px;border-radius:50%;background:#22d3ee;cursor:pointer}
        input[type=range].demo-vol::-moz-range-thumb{width:10px;height:10px;border-radius:50%;background:#22d3ee;border:none;cursor:pointer}
      `}</style>
      <Blobs />
      <EcgLine />
      <div key={sceneKey} className="absolute inset-0 pb-14" style={{ animation: 'sceneIn 0.7s cubic-bezier(0.22,1,0.36,1)' }}>{scene}</div>
      <div className="absolute bottom-8 left-0 right-0 px-4 text-center pointer-events-none">
        <span className="inline-block bg-black/60 text-white text-xs md:text-sm font-medium px-3 py-1.5 rounded-lg" data-testid="demo-ext-subtitle">{sub}</span>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-slate-800">
        <div className="h-full bg-gradient-to-r from-cyan-500 to-blue-500" style={{ width: `${(t / DURATION) * 100}%`, animation: 'glowBar 2s ease-in-out infinite' }} />
      </div>
      {!capture && (
        <>
          <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-black/60 backdrop-blur rounded-lg p-1 z-20">
            <button onClick={() => setPlaying(!playing)} aria-label="Reproducir/Pausar" className="text-white hover:text-cyan-400 p-1.5" data-testid="ext-play-btn">
              {playing ? <Pause size={15} /> : <Play size={15} />}
            </button>
            <button onClick={restart} aria-label="Reiniciar" className="text-white hover:text-cyan-400 p-1.5" data-testid="ext-restart-btn"><RotateCcw size={15} /></button>
            {hasSound && (
              <button onClick={toggleSound} aria-label="Sonido" className="text-white hover:text-cyan-400 p-1.5" data-testid="ext-sound-btn">
                {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
              </button>
            )}
            {hasSound && !muted && (
              <div className="flex items-center gap-2 px-1.5" data-testid="ext-volume-controls">
                <div className="flex items-center gap-1" title="Volumen narración">
                  <Mic size={12} className="text-cyan-400" />
                  <input type="range" min="0" max="1" step="0.05" value={volVoice} className="demo-vol"
                    onChange={e => setVolVoice(parseFloat(e.target.value))} data-testid="ext-voice-volume" />
                </div>
                <div className="flex items-center gap-1" title="Volumen música">
                  <Music size={12} className="text-purple-400" />
                  <input type="range" min="0" max="1" step="0.05" value={volMusic} className="demo-vol"
                    onChange={e => setVolMusic(parseFloat(e.target.value))} data-testid="ext-music-volume" />
                </div>
              </div>
            )}
            <button onClick={() => document.fullscreenElement ? document.exitFullscreen() : boxRef.current?.requestFullscreen?.()}
              aria-label="Pantalla completa" className="text-white hover:text-cyan-400 p-1.5" data-testid="ext-fullscreen-btn"><Maximize size={15} /></button>
            <span className="text-slate-300 text-[10px] font-mono px-1">{Math.floor(t / 60)}:{String(Math.floor(t % 60)).padStart(2, '0')} / 2:02</span>
          </div>
          {hasSound && muted && (
            <button onClick={toggleSound} className="absolute bottom-2 right-2 z-20 bg-cyan-500/90 hover:bg-cyan-400 text-white text-xs font-semibold px-3 py-1.5 rounded-lg" data-testid="ext-listen-btn">
              Activar audio
            </button>
          )}
        </>
      )}
      {capture && !started && (
        <button onClick={startCapture} className="absolute inset-0 z-30 bg-white" data-testid="capture-splash" aria-label="Iniciar captura" />
      )}
      {withAudio && audioOk && <audio ref={audioRef} src={`${API_URL}/api/videos/assets-preview/demo_narracion`} preload="metadata" muted />}
      {withAudio && musicOk && <audio ref={musicRef} src={`${API_URL}/api/videos/assets-preview/musica`} preload="metadata" muted loop />}
    </div>
  );
});

export default AnimatedDemoExtended;
